import { useEffect, useRef } from "react";
import Api from "../../../utils/Api";
import { BASE_API_URL, DYNAMIC_QR } from "../../../constants/api-path.c";
import { SERVER_ERROR_PAGE, SUCCESS_PAGE } from "../../../constants/path.c";
import {
  QRPH_TIMER,
  QRPH_TRANSACTION_STATUS,
} from "../../../constants/qrph.c";
import { getLanguagePrefix } from "../../hooks/getLanguagePrefix";

type UseQrphStatusPollingArgs = {
  transactionId?: string;
  navigate?: (path: string) => void;
  expired: boolean;
  /** Restarts polling (and re-arms the redirect guard) when it changes. */
  resetKey?: unknown;
};

/**
 * Polls the Qrph transaction status while the QR is displayed and redirects
 * to the success/error page once the transaction reaches a terminal status.
 */
export const useQrphStatusPolling = ({
  transactionId,
  navigate,
  expired,
  resetKey,
}: UseQrphStatusPollingArgs) => {
  const redirectedRef = useRef(false);

  useEffect(() => {
    redirectedRef.current = false;
  }, [resetKey]);

  useEffect(() => {
    if (!transactionId || !navigate || expired || redirectedRef.current) {
      return;
    }

    const redirectTo = (path: string) => {
      if (redirectedRef.current) return;
      redirectedRef.current = true;
      navigate(`${getLanguagePrefix()}${path}`);
    };

    const pollStatus = async () => {
      if (redirectedRef.current) return;
      try {
        const data = await Api().get(
          `${BASE_API_URL}/${DYNAMIC_QR.GET_TRANSACTION_STATUS.replace(
            ":transactionId",
            transactionId
          )}`
        );

        const status = data?.data;
        if (QRPH_TRANSACTION_STATUS.SUCCESS.includes(status)) {
          redirectTo(`/${SUCCESS_PAGE}/${transactionId}`);
        } else if (QRPH_TRANSACTION_STATUS.FAILURE.includes(status)) {
          redirectTo(`/${SERVER_ERROR_PAGE}/${transactionId}`);
        }
      } catch {
        // Keep polling until the timer expires; transient errors are expected
        // while waiting for the Qrph credit notification.
      }
    };

    pollStatus();
    const id = window.setInterval(pollStatus, QRPH_TIMER.STATUS_POLL_MS);
    return () => window.clearInterval(id);
  }, [transactionId, navigate, expired, resetKey]);
};
